import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, X, FileCheck } from "lucide-react";

interface ReviewData {
  reviewNotes: string;
  timestamp: string;
}

interface ReviewTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReview: (reviewData: ReviewData) => void;
  templateName: string;
  initialReviewNotes?: string;
  isDarkMode?: boolean;
}

export function ReviewTemplateModal({
  isOpen,
  onClose,
  onReview,
  templateName,
  initialReviewNotes = "",
  isDarkMode = false,
}: ReviewTemplateModalProps) {
  // Form state
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewNotesError, setReviewNotesError] = useState("");

  useEffect(() => {
    if (isOpen) {
      // Prefill with latest saved draft so users can continue editing.
      setReviewNotes(initialReviewNotes);
      setReviewNotesError("");
    }
  }, [isOpen, initialReviewNotes]);


  const handleSubmit = () => {
    // Validation
    if (!reviewNotes.trim()) {
      setReviewNotesError("Review notes are required.");
      return;
    }

    setReviewNotesError("");

    // Create review data
    const reviewData: ReviewData = {
      reviewNotes: reviewNotes.trim(),
      timestamp: new Date().toISOString(),
    };

    onReview(reviewData);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${
        isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white"
      }`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}>
            <FileCheck className="w-5 h-5 text-green-600" />
            Review Template: {templateName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Instructions */}
          <div className={`p-4 rounded-lg border ${
            isDarkMode
              ? "bg-blue-900/20 border-blue-700 text-blue-200"
              : "bg-blue-50 border-blue-200 text-blue-800"
          }`}>
            <h4 className="font-medium mb-2">Template Review Instructions</h4>
            <p className="text-sm">
              Please review this consultation template carefully and add your review notes below.
              The template will be marked as reviewed and approved for clinical use.
            </p>
          </div>

          {/* Review Notes */}
          <div className="space-y-2">
            <Label className={`text-sm font-medium ${
              isDarkMode ? "text-gray-200" : "text-gray-900"
            }`}>
              Review Notes *
            </Label>
            <Textarea
              value={reviewNotes}
              onChange={(e) => {
                const value = e.target.value;
                setReviewNotes(value);
                if (value.trim()) {
                  setReviewNotesError("");
                }
              }}
              placeholder="Enter your review comments, findings, and any recommendations..."
              rows={4}
              aria-invalid={Boolean(reviewNotesError)}
              className={`resize-none ${
                reviewNotesError
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              } ${
                isDarkMode 
                  ? "bg-gray-800 border-gray-600 text-white placeholder:text-gray-400" 
                  : "bg-white border-gray-300"
              }`}
            />
            {reviewNotesError && (
              <p className="text-sm text-red-500">{reviewNotesError}</p>
            )}
          </div>


          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <Check className="w-4 h-4 mr-2" />
              Submit Review
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
